import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { btnPrimary, btnOutline, overline } from "@/components/kit";
import { track } from "@/lib/track";
import { ArrowRight, ArrowUpRight, Check, TreePine } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { CONTACT } from "@/data/site";

const FAQ = [
  { q: "Proposez-vous la location de sapin pour les entreprises ?", a: "Oui : nous livrons, installons et décorons le sapin dans vos locaux — naturel ou réutilisable — puis nous le reprenons en janvier." },
  { q: "Quel budget pour décorer nos bureaux pour Noël ?", a: "Le format « Noël dans vos locaux » avec décoration, sapin et animations démarre à 6 900 € HT. Nous ajustons selon la surface, le nombre d'espaces et le niveau de finition souhaité." },
  { q: "L'installation perturbe-t-elle le travail des équipes ?", a: "Non : nous intervenons hors horaires de travail — tôt le matin, le soir ou le week-end — et le démontage est inclus en janvier." },
  { q: "Pouvez-vous décorer plusieurs sites en même temps ?", a: "Oui, nous coordonnons la décoration de plusieurs sites avec un interlocuteur unique et un cahier des charges commun." },
];

const SERVICE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Décoration de Noël pour entreprises",
  description: "Décoration de Noël pour entreprises : location et installation de sapin, décoration de halls et bureaux, mise en lumière, installation hors horaires et démontage en janvier.",
  provider: {
    "@type": "LocalBusiness",
    name: "Désir Events",
    url: "https://desir-events.com",
    telephone: CONTACT.phoneFixe,
    email: CONTACT.email,
    address: { "@type": "PostalAddress", streetAddress: "129 rue de Servient", addressLocality: "Lyon", postalCode: "69003", addressCountry: "FR" },
  },
  areaServed: { "@type": "Country", name: "France" },
};

const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
};

const BREADCRUMB_JSONLD = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Accueil", item: "https://desir-events.com/" },
    { "@type": "ListItem", position: 2, name: "Noël en entreprise" },
    { "@type": "ListItem", position: 3, name: "Décoration Noël entreprise", item: "https://desir-events.com/decoration-noel-entreprise" },
  ],
};

const PRESTATIONS = [
  "Location et installation de sapin (naturel ou réutilisable)",
  "Décoration de hall d'accueil et de réception",
  "Vitrophanie et décoration des baies vitrées",
  "Mise en lumière extérieure de votre façade",
  "Décoration suspendue et arches d'entrée",
  "Coin photo de Noël pour vos équipes",
  "Scénographie de vos espaces de vie (cafétéria, open space)",
  "Installation hors horaires de travail",
  "Démontage et reprise du matériel en janvier",
];

export default function DecorationNoelEntreprise() {
  return (
    <>
      <Seo
        title="Décoration Noël entreprise | Bureaux, halls et réceptions"
        description="Décoration de Noël pour entreprises : location et installation de sapin, décoration de halls et bureaux, mise en lumière, démontage en janvier. Noël dans vos locaux dès 6 900 € HT avec animations."
        path="/decoration-noel-entreprise"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(SERVICE_JSONLD)}</script>
        <script type="application/ld+json">{JSON.stringify(FAQ_JSONLD)}</script>
        <script type="application/ld+json">{JSON.stringify(BREADCRUMB_JSONLD)}</script>
      </Helmet>
      <PageHero eyebrow="Noël · Vos locaux" title={<>Décoration de Noël pour entreprises :<br /><span className="italic text-lime-dim">bureaux, halls et réceptions</span></>} />

      <section className="mx-auto max-w-[1440px] px-5 py-16 md:px-10" data-testid="deco-entreprise-intro">
        <Reveal>
          <p className="max-w-3xl text-lg leading-relaxed text-ink/70">
            Un hall décoré change l'atmosphère de décembre — pour les équipes comme pour les visiteurs. Nous livrons, installons et décorons sapins et scénographies dans vos locaux, hors de vos horaires de travail, puis nous démontons tout en janvier.
          </p>
        </Reveal>
        <Reveal delay={1}>
          <div className="mt-8 flex flex-wrap items-center gap-4 rounded-2xl border border-lime-dim/40 bg-lime/10 p-6">
            <TreePine size={26} className="shrink-0 text-lime-dim" />
            <p className="text-sm leading-relaxed text-ink/75">
              <strong>« Noël dans vos locaux » dès 6 900 € HT</strong> — décoration complète, sapin, animations et coordination pour un événement de fin d'année sans quitter l'entreprise. Décoration seule : sur devis selon vos espaces.
            </p>
          </div>
        </Reveal>
      </section>

      <section className="border-t border-ink/10 bg-mist" data-testid="deco-entreprise-prestations">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-10 md:py-24">
          <Reveal><span className={overline}>Ce que nous installons</span></Reveal>
          <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {PRESTATIONS.map((p) => (
              <div key={p} className="flex items-start gap-3 rounded-xl border border-ink/10 bg-white px-5 py-4">
                <Check size={15} className="mt-0.5 shrink-0 text-lime-dim" />
                <p className="text-sm font-medium text-ink">{p}</p>
              </div>
            ))}
          </div>
          <Reveal delay={2}>
            <p className="mt-8 max-w-2xl text-sm text-ink/50">Pour les sièges multi-étages et les groupes : un concept commun décliné par site, avec planning d'installation mutualisé. Chaque devis détaille location, fabrication, pose et démontage.</p>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-ink/10 bg-white" data-testid="deco-entreprise-faq">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-10 md:py-24">
          <Reveal><span className={overline}>Questions fréquentes</span></Reveal>
          <div className="mt-10 grid gap-4 md:grid-cols-2">
            {FAQ.map((f, i) => (
              <Reveal key={f.q} delay={i % 2}>
                <div className="h-full rounded-2xl border border-ink/10 bg-mist p-7" data-testid={`deco-entreprise-faq-${i}`}>
                  <h3 className="font-serif text-xl leading-snug text-ink">{f.q}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink/60">{f.a}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Maillage */}
      <section className="border-t border-ink/10 bg-white" data-testid="deco-entreprise-related">
        <div className="mx-auto max-w-[1440px] px-5 py-14 md:px-10">
          <Reveal><span className={overline}>Autour de Noël</span></Reveal>
          <div className="mt-6 flex flex-wrap gap-3">
            {[
              { label: "Soirée de Noël d'entreprise à Lyon", to: "/fete-de-noel-entreprise/lyon" },
              { label: "Animations de Noël", to: "/animation-noel-entreprise" },
              { label: "Décoration de Noël pour magasins", to: "/decoration-noel-magasin" },
              { label: "Arbre de Noël CSE à Lyon", to: "/evenements-cse/lyon" },
            ].map((r) => (
              <Link key={r.to} to={r.to} className="group flex items-center gap-2 rounded-full border border-ink/15 bg-mist px-5 py-2.5 text-sm text-ink/70 transition-colors hover:border-lime-dim hover:text-ink" data-testid={`deco-ent-related-${r.to.replace(/\//g, "")}`}>
                {r.label} <ArrowUpRight size={14} className="text-lime-dim" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-5 py-20 md:px-10 md:py-28" data-testid="deco-entreprise-cta">
        <Reveal><h2 className="max-w-3xl font-serif text-4xl font-medium leading-tight tracking-tight text-ink md:text-5xl">Noël dans vos locaux, <span className="italic text-lime-dim">clés en main.</span></h2></Reveal>
        <Reveal delay={2}>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/devis" onClick={() => track("click_devis")} className={btnPrimary} data-testid="deco-entreprise-devis-btn">Recevoir une première proposition <ArrowRight size={16} /></Link>
            <a href={`tel:${CONTACT.phoneMobile.replace(/\s/g, "")}`} onClick={() => track("click_phone")} className={btnOutline}>Appeler le {CONTACT.phoneMobile}</a>
          </div>
        </Reveal>
      </section>
    </>
  );
}
